import React, { useState, useMemo, useEffect } from 'react';
import { Trans } from '@lingui/react/macro';
import { ButtonProps } from '@ygtechnologies/rubus-ui';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { ValidationScope } from '@/components/forms/validation';
import { useValidationManager } from '@/utils/hooks';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { approveExternalAgreement, ApproveExternalAgreementChangeRequestOptions } from '@/services/external-agreements';
import { SimpleWizard, SimpleWizardStep } from '@ygtechnologies/bygr-frontend-component-library';
import { ChangeRequestParticipantSelectListItem } from '@/seller/components/change-requests/ChangeRequestParticipantSelectList';
import { ExternalAgreement, OptionGroupOverrideSelection } from '@/types';
import ExternalAgreementApproveWizardChangeRequest from './ExternalAgreementApproveWizardChangeRequest';
import ExternalAgreementApproveWizardParticipants from './ExternalAgreementApproveWizardParticipants';
import ExternalAgreementApproveWizardOverrideOptions from './ExternalAgreementApproveWizardOverrideOptions';
import ExternalAgreementApproveWizardOffer, {
    ExternalAgreementApproveOfferOptions,
} from './ExternalAgreementApproveWizardOffer';
import ExternalAgreementApproveWizardSummary from './ExternalAgreementApproveWizardSummary';

enum Step {
    ChangeRequest,
    Participants,
    Offer,
    OverrideOptions,
    Summary,
}

export enum ExternalAgreementSignMode {
    Unset,
    PreSigned,
    Unsigned,
}

interface ExternalAgreementApproveWizardProps {
    externalAgreement: ExternalAgreement;
    onSaved(): void;
    onCancel(): void;
    onBack?(): void;
}

function getEmptyChangeRequestOptions(
    initialSubject: string,
    description: string = '',
    internalDescription: string = ''
): ApproveExternalAgreementChangeRequestOptions {
    return {
        subject: initialSubject || '',
        description: description || '',
        internalDescription: internalDescription || '',
    };
}

const ExternalAgreementApproveWizard: React.FC<ExternalAgreementApproveWizardProps> = ({
    externalAgreement,
    onCancel,
    onSaved,
}) => {
    const projectId = useSelector((state: IState) => state.global.activeProject!.id);
    const [step, setStep] = useState(Step.ChangeRequest);

    const [selectedParticipants, setSelectedParticipants] = useState<ChangeRequestParticipantSelectListItem[]>([]);
    const [changeRequestOptions, setChangeRequestOptions] = useState<ApproveExternalAgreementChangeRequestOptions>(
        getEmptyChangeRequestOptions('')
    );
    const [saving, setSaving] = useState(false);
    const [overrideBuyerOptionGroupIds, setOverrideBuyerOptionGroupIds] = useState<OptionGroupOverrideSelection[]>([]);
    const [offerOptions, setOfferOptions] = useState(ExternalAgreementApproveOfferOptions.empty());

    useEffect(() => {
        setStep(Step.ChangeRequest);
        setChangeRequestOptions(
            getEmptyChangeRequestOptions(externalAgreement.name, '', externalAgreement.participantsNote)
        );
        setSelectedParticipants(externalAgreement.participants.map((p) => ({ userId: p.userId, profileId: p.profileId })));
        setOverrideBuyerOptionGroupIds([]);
        setOfferOptions(ExternalAgreementApproveOfferOptions.empty());
    }, [externalAgreement]);

    const handleParticipantsChange = (newValue: ChangeRequestParticipantSelectListItem[]) => {
        const creator = newValue.find((x) => x.userId === externalAgreement.createdByUser.id);

        if (!creator) {
            return;
        }

        setSelectedParticipants(newValue);
    };

    const saveButtonProps: Partial<ButtonProps> = useMemo(
        () => ({
            children: <Trans>Approve</Trans>,
            loading: saving,
            variant: 'success',
        }),
        [saving]
    );

    const validationManager = useValidationManager();

    const Steps =
        selectedParticipants.length > 1
            ? [Step.ChangeRequest, Step.Participants, Step.Summary]
            : [Step.ChangeRequest, Step.Participants, Step.OverrideOptions, Step.Offer, Step.Summary];

    const validate = () => {
        var errors = validationManager.validate(true);

        return errors.length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) {
            return;
        }

        setSaving(true);

        try {
            await approveExternalAgreement(externalAgreement.id, {
                changeRequestOptions,
                participants: selectedParticipants,
                overrideBuyerOptionGroupIds,
                offerSendNote: offerOptions.offerMessage,
                preSigned: offerOptions.signMode === ExternalAgreementSignMode.PreSigned,
                showSubtotalsToBuyers: offerOptions.showSubtotalsToBuyers,
            });

            triggerHandler(onSaved);
        } finally {
            setSaving(false);
        }
    };

    return (
        <ValidationScope validationManager={validationManager}>
            <SimpleWizard
                steps={Steps}
                currentStep={step}
                onStepChange={(step) => setStep(step)}
                onSubmit={handleSubmit}
                onCancel={onCancel}
                saveButtonProps={saveButtonProps}>
                {/*<SimpleWizardStep stepNumber={Step.ChangeRequest} header={<Trans>Approving an external agreement</Trans>}>
                    <ExternalAgreementApproveWizardInfo externalAgreement={externalAgreement} />
                </SimpleWizardStep>*/}

                <SimpleWizardStep stepNumber={Step.ChangeRequest} header={<Trans>Create change request</Trans>}>
                    <ExternalAgreementApproveWizardChangeRequest
                        projectId={projectId}
                        value={changeRequestOptions}
                        onChange={setChangeRequestOptions}
                    />
                </SimpleWizardStep>

                <SimpleWizardStep stepNumber={Step.Participants} header={<Trans>Add participants</Trans>}>
                    <ExternalAgreementApproveWizardParticipants
                        projectId={projectId}
                        value={selectedParticipants}
                        onChange={handleParticipantsChange}
                    />
                </SimpleWizardStep>

                <SimpleWizardStep stepNumber={Step.OverrideOptions} header={<Trans>Select options to override</Trans>}>
                    <ExternalAgreementApproveWizardOverrideOptions
                        residentialUnitId={externalAgreement.residentialUnitId}
                        value={overrideBuyerOptionGroupIds}
                        onChange={setOverrideBuyerOptionGroupIds}
                    />
                </SimpleWizardStep>

                <SimpleWizardStep stepNumber={Step.Offer} header={<Trans>Add message to buyer</Trans>}>
                    <ExternalAgreementApproveWizardOffer
                        hasSingleParticipant={selectedParticipants.length === 1}
                        onChange={setOfferOptions}
                        data={offerOptions}
                    />
                </SimpleWizardStep>

                <SimpleWizardStep stepNumber={Step.Summary} header={<Trans>Summary</Trans>}>
                    <ExternalAgreementApproveWizardSummary
                        changeRequestOptions={changeRequestOptions}
                        offerOptions={offerOptions}
                        selectedParticipants={selectedParticipants}
                        externalAgreement={externalAgreement}
                    />
                </SimpleWizardStep>
            </SimpleWizard>
        </ValidationScope>
    );
};

export default ExternalAgreementApproveWizard;